import {
  string as yupString,
  boolean as yupBoolean,
  array as yupArray,
} from 'yup';

import SelectFormik from '../../shared/forms/select/sdkSelectFormik.component';
import InputFormik from '../../shared/forms/input/inputFormik.component';
import CheckboxFormik from '../../shared/forms/checkbox/checkboxFormik.component';
import AttachmentsFormik from '../../shared/forms/attachments/attachmentsFormik.component';
import { hideField } from '../../shared/forms/constants';
import { yupAmount } from './helpers';
import { required, requiredWithFlagInContext, differentThan, arrayMinLength } from '../../shared/forms/validators';

import AmountFormik from './amountFormik.component';
import reclassBalancesSchema from './reclassBalances.schema';

const resetCountryTaxType = (_, { formik }) => {
  formik.setFieldTouched('countryTaxType', false);
  formik.setFieldValue('countryTaxType', '');
};

const disableFieldsIfTBEError = {
  when: ({ formContext, formik }) =>
    formContext.tbeErrors.find(tbeError => tbeError.companyCode === formik.values.companyCode),
  then: { additionalProps: { disabled: true } },
};

const fbosValueGetter = ({ formContext }) => formContext.combination.fbosFlag || false;

const checkTbeErrors = (_, validator, validationContext) =>
  validator.test('checkTbeErrors', function({ value }) {
    return validationContext.tbeErrors.find(tbeError => tbeError.companyCode === value)?.tbeError;
  },
  function(value) {
    const tbeError = validationContext.tbeErrors.find(tbeError => tbeError.companyCode === value);
    return Boolean(!tbeError);
  });

const schema = [
  {
    name: 'companyCode',
    component: SelectFormik,
    meta: { label: 'Company Code', optionsKey: 'companyCodes' },
    options: { yupType: yupString(), validationRules: [required, checkTbeErrors] },
    additionalProps: {
      required: true, virtualized: true, className: 'left-column',
      onSelect: resetCountryTaxType,
    },
    conditions: [{
      when: ({ formContext: { isLoadingTbeErrors } }) => isLoadingTbeErrors,
      then: { additionalProps: { loading: true } },
    }],
  },
  {
    name: 'taxYear',
    component: SelectFormik,
    meta: { label: 'Tax Year', optionsKey: 'taxYears' },
    options: { yupType: yupString(), validationRules: [required] },
    additionalProps: { required: true, virtualized: true, className: 'left-column' },
    conditions: [disableFieldsIfTBEError],
  },
  {
    name: 'countryTaxType',
    component: SelectFormik,
    meta: { label: 'Country Tax Type', optionsKey: 'countryTaxTypes' },
    options: { yupType: yupString(), validationRules: [required] },
    additionalProps: { required: true, virtualized: true, className: 'left-column' },
    conditions: [
      disableFieldsIfTBEError,
      {
        when: ({ formContext: { isLoadingCountryTaxTypes } }) => isLoadingCountryTaxTypes,
        then: { additionalProps: { loading: true } },
      },
    ],
  },
  {
    name: 'entryType',
    component: SelectFormik,
    meta: { label: 'Entry Type', optionsKey: 'entryTypes' },
    options: { yupType: yupString(), validationRules: [required] },
    additionalProps: { required: true, virtualized: true, clearWhenOptionsChanged: true, className: 'left-column' },
    conditions: [
      disableFieldsIfTBEError,
      {
        when: ({ formContext: { isLoadingTaxEntriesTree, isLoadingTypeDescriptions } }) =>
          isLoadingTaxEntriesTree || isLoadingTypeDescriptions,
        then: { additionalProps: { loading: true } },
      },
    ],
  },
  {
    name: 'entryActivity',
    component: SelectFormik,
    meta: { label: 'Activity', optionsKey: 'entryActivities' },
    options: { yupType: yupString(), validationRules: [required] },
    additionalProps: { required: true, virtualized: true, clearWhenOptionsChanged: true, className: 'left-column' },
    conditions: [
      disableFieldsIfTBEError,
      {
        when: ({ formContext: { isLoadingTaxEntriesTree, isLoadingActivityDescriptions } }) =>
          isLoadingTaxEntriesTree || isLoadingActivityDescriptions,
        then: { additionalProps: { loading: true } },
      },
    ],
  },
  {
    name: 'bookType',
    component: SelectFormik,
    meta: { label: 'Book Type', optionsKey: 'bookTypes' },
    options: { yupType: yupString(), validationRules: [required] },
    additionalProps: { required: true, virtualized: true, clearWhenOptionsChanged: true, className: 'left-column' },
    conditions: [
      disableFieldsIfTBEError,
      {
        when: ({ formContext: { isLoadingTaxEntriesTree } }) => isLoadingTaxEntriesTree,
        then: { additionalProps: { loading: true } },
      },
    ],
  },
  {
    name: 'account',
    component: SelectFormik,
    meta: { label: 'Account', optionsKey: 'accounts' },
    options: { yupType: yupString(), validationRules: [
      requiredWithFlagInContext('requireAccount'),
      differentThan('toAccount', 'To Account'),
    ] },
    additionalProps: { virtualized: true, className: 'left-column' },
    conditions: [
      {
        when: ({ formContext }) => formContext.combination.accountOverride,
        then: { additionalProps: { disabled: true } },
      },
      {
        when: ({ formContext }) => formContext.combination.requireAccount,
        then: { additionalProps: { required: true } },
        otherwise: hideField,
      },
    ],
  },
  {
    name: 'amount',
    component: AmountFormik,
    meta: { label: 'Amount', initialValue: { net: '0.00', gross: '' } },
    options: { yupType: yupAmount, validationRules: [] },
    additionalProps: ({
      currencyCode,
      deferredTaxRate,
      stateApportionmentRate,
      combination,
    }) => ({
      className: 'left-column',
      suffix: currencyCode,
      deferredTaxRate,
      stateApportionmentRate,
      allowGross: combination.allowGross,
      isUsState: combination.isUsState,
    }),
    conditions: [disableFieldsIfTBEError],
  },
  {
    name: 'fbosFlag',
    component: CheckboxFormik,
    meta: { label: 'Generate matching State and FBOS Entries', initialValue: false },
    options: { yupType: yupBoolean() },
    additionalProps: { valueFormatter: Boolean, className: 'left-column' },
    conditions: [{
      when: ({ formContext }) => !formContext.combination.allowSelectingFbosFlag,
      then: {
        ...hideField,
        meta: { valueGetter: fbosValueGetter },
      },
    }],
  },
  {
    name: 'topsideFlag',
    component: CheckboxFormik,
    meta: { label: 'Topside', initialValue: false },
    options: { yupType: yupBoolean() },
    additionalProps: { valueFormatter: Boolean, className: 'left-column' },
    conditions: [
      {
        when: ({ formContext }) => !formContext.combination.allowTopside,
        then: hideField,
      },
      {
        when: ({ formContext }) => formContext.combination.forceTopside,
        then: {
          additionalProps: { disabled: true },
        },
      },
    ],
  },
  ...reclassBalancesSchema,
  {
    name: 'payableTag',
    component: SelectFormik,
    meta: { label: 'Payable Group', optionsKey: 'payableTags', initialValue: '' },
    options: { yupType: yupString(), validationRules: [
      requiredWithFlagInContext('allowSelectingPayableTag'),
    ] },
    additionalProps: {
      virtualized: true,
      clearWhenOptionsChanged: true,
      className: 'right-column',
    },
    conditions: [{
      when: ({ formContext }) => formContext.combination.allowSelectingPayableTag,
      then: { additionalProps: { required: true } },
      otherwise: hideField,
    }],
  },
  {
    name: 'glAccount',
    component: SelectFormik,
    meta: { label: 'GL Account Offset', optionsKey: 'glAccounts', initialValue: '' },
    options: { yupType: yupString(), validationRules: [
      requiredWithFlagInContext('allowSelectingGlAccount'),
    ] },
    additionalProps: { virtualized: true, clearWhenOptionsChanged: true, className: 'right-column' },
    conditions: [{
      when: ({ formContext }) => formContext.combination.allowSelectingGlAccount,
      then: { additionalProps: { required: true } },
      otherwise: hideField,
    }],
  },
  {
    name: 'comment',
    component: InputFormik,
    meta: { label: 'Comment' },
    options: { yupType: yupString() },
    additionalProps: { className: 'right-column' },
    conditions: [disableFieldsIfTBEError],
  },
  {
    name: 'attachments',
    component: AttachmentsFormik,
    meta: { label: 'Attachments', initialValue: [] },
    options: { yupType: yupArray(), validationRules: [arrayMinLength(1, 'At least one attachment is required')] },
    additionalProps: {
      valueFormatter: file => file,
      multiple: true,
      acceptTypes: ['.xls', '.xlsx', '.doc', '.docx', '.ppt', '.pptx', '.pdf', '.txt'],
      className: 'right-column tall',
      required: true,
    },
    conditions: [disableFieldsIfTBEError],
  },
];

export default schema;
